import React from "react";
import CONTACT from "../../assets/images/contact.png";
import { FieldArray, FormikProps } from "formik";
import { useAppSelector } from "../../app/hooks";
import { IChild, IReviewBookingDetails } from "./reviewbookingModel";
import { INITIALS } from "../../_constants/constant";
import moment from "moment";
import { getFieldError, getSelectedItems } from "../../_helpers/miscUtils";
import { handleUppercaseChange } from "../../_helpers/formatUtils";
import { FormField } from "../../components/formfield/FormField";

interface ChildFormProps {
  formik: FormikProps<IReviewBookingDetails>;
  ChildRequireDetails: any;
  CHILD: number;
  fieldRefs: React.MutableRefObject<any>;
  handleCheckboxChange: (
    type: "adults" | "children" | "infants",
    flightDetailsKey: "ADULT" | "CHILD" | "INFANT",
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
}

const ChildForm: React.FC<ChildFormProps> = ({
  formik,
  handleCheckboxChange,
  ChildRequireDetails,
  CHILD,
  fieldRefs,
}) => {
  const reviewBookingDetails = useAppSelector(
    (state) => state.reviewbooking.reviewBookingDetails
  );
  const CountryList = useAppSelector((state) => state.country.CountryList);

  const renderTextInput = (
    index: number,
    fieldName: keyof IChild,
    label: string,
    required: boolean,
    uppercase = false
  ) => {
    const fieldPath = `children[${index}].${fieldName}`;
    const error = getFieldError<IChild>(
      formik.errors.children,
      index,
      fieldName
    );
    const touched = formik.touched.children?.[index]?.[fieldName] as
      | boolean
      | undefined;

    return (
      <FormField
        label={label}
        required={required}
        error={error}
        touched={touched}
      >
        <input
          type="text"
          className={touched && error ? "error-border" : ""}
          name={fieldPath}
          // ref={(el) => (fieldRefs.current[fieldPath] = el)}
          onChange={
            uppercase
              ? (e) => handleUppercaseChange(formik, e)
              : formik.handleChange
          }
          value={formik.values.children[index][fieldName] as string}
        />
      </FormField>
    );
  };

  const renderDateInput = (
    index: number,
    fieldName: keyof IChild,
    label: string,
    required: boolean,
    minDate?: string,
    maxDate?: string
  ) => {
    const fieldPath = `children[${index}].${fieldName}`;
    const error = getFieldError<IChild>(
      formik.errors.children,
      index,
      fieldName
    );
    const touched = formik.touched.children?.[index]?.[fieldName] as
      | boolean
      | undefined;

    return (
      <FormField
        label={label}
        required={required}
        error={error}
        touched={touched}
      >
        <input
          type="date"
          name={fieldPath}
          // ref={(el) => (fieldRefs.current[fieldPath] = el)}
          onChange={formik.handleChange}
          min={minDate}
          max={maxDate}
          value={formik.values.children[index][fieldName] as string}
          className={touched && error ? "error-border" : ""}
        />
      </FormField>
    );
  };

  const renderSelectInput = (
    index: number,
    fieldName: keyof IChild,
    label: string,
    required: boolean,
    options: Array<{ value: string; label: string }>,
    placeholder: string
  ) => {
    const fieldPath = `children[${index}].${fieldName}`;
    const error = getFieldError<IChild>(
      formik.errors.children,
      index,
      fieldName
    );
    const touched = formik.touched.children?.[index]?.[fieldName] as
      | boolean
      | undefined;

    return (
      <FormField
        label={label}
        required={required}
        error={error}
        touched={touched}
      >
        <select
          name={fieldPath}
          // ref={(el) => (fieldRefs.current[fieldPath] = el)}
          value={formik.values.children[index][fieldName] as string}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className={touched && error ? "error-border py-0" : "py-0"}
        >
          <option value="" hidden>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </FormField>
    );
  };

  const renderPersonalInfoSection = (index: number) => (
    <div className="field-set align-end">
      {renderSelectInput(
        index,
        "initial",
        "Initial",
        true,
        INITIALS.map((e) => ({ value: e, label: e })),
        "Select Initial"
      )}
      {renderTextInput(index, "first_name", "First Name", true, true)}
      {renderTextInput(index, "last_name", "Last Name", true, true)}
    </div>
  );

  const renderPassportInfoSection = (index: number) => (
    <div className="field-set">
      {renderDateInput(
        index,
        "date_of_birth",
        "Date of Birth",
        true,
        moment().subtract(12, "years").format("YYYY-MM-DD"),
        moment().subtract(2, "years").format("YYYY-MM-DD")
      )}
      {ChildRequireDetails?.Passport_Number &&
        renderTextInput(index, "passport_no", "Passport No", true, true)}
      {!ChildRequireDetails?.Passport_Number && <div className="fields"></div>}
      {ChildRequireDetails?.Passport_Issuing_Country &&
        renderSelectInput(
          index,
          "issue_country_code",
          "Issue Country",
          true,
          CountryList?.map((e) => ({
            value: e.country_code,
            label: e.country_name,
          })),
          "Select Country"
        )}
      {!ChildRequireDetails?.Passport_Issuing_Country && (
        <div className="fields"></div>
      )}
    </div>
  );

  const renderAdditionalPassportInfoSection = (index: number) => (
    <div className="field-set mb-3">
      {ChildRequireDetails?.Passport_Expiry &&
        renderDateInput(
          index,
          "expire_date",
          "Passport Expiry Date",
          true,
          moment().add(6, "months").format("YYYY-MM-DD"),
          ""
        )}
      {ChildRequireDetails?.Nationality &&
        renderSelectInput(
          index,
          "nationality",
          "Nationality",
          true,
          CountryList?.map((e) => ({
            value: e.country_code,
            label: e.country_name,
          })),
          "Select Country"
        )}
      {ChildRequireDetails?.PanCard_No &&
        renderTextInput(index, "pancard_number", "Pancard No", true, true)}
      {!ChildRequireDetails?.PanCard_No && <div className="fields"></div>}
    </div>
  );

  const renderStudentInfoSection = (index: number) => (
    <div className="field-set mb-3">
      {ChildRequireDetails?.Student_Id &&
        renderDateInput(
          index,
          "student_id",
          "Student ID",
          true,
          "",
          moment().format("YYYY-MM-DD")
        )}
      <div className="fields"></div>
      <div className="fields"></div>
    </div>
  );

  const getChildDisplayName = (child: IChild, index: number) => {
    return reviewBookingDetails?.children[index]?.first_name
      ? `${child.first_name} ${child.last_name}`
      : `Child ${
          index -
          (getSelectedItems(reviewBookingDetails?.children, "first_name")
            ?.length -
            1)
        }`;
  };

  return (
    <div className="adult-form">
      <div className="traveller-details-title">
        <p className="m-0">
          <img src={CONTACT} alt="contact" /> CHILD (2-12 Yrs)
          <span className="m-0 tarveller-error-message">
            {formik.errors?.childrenLimit}
          </span>
        </p>
        <strong>
          {getSelectedItems(formik.values?.children)?.length}/{CHILD} added
        </strong>
      </div>
      <span className="note">
        <strong>Important: </strong>Enter name as mentioned on your passport or
        Government approved IDs.
      </span>
      <FieldArray name="children">
        {({ push, remove }) => (
          <>
            {formik.values.children.map((child, index) => (
              <div className="child-form" key={index}>
                <div>
                  <input
                    className="form-check"
                    type="checkbox"
                    name={`children[${index}].isSelected`}
                    checked={
                      formik.values.children[index].isSelected as boolean
                    }
                    onChange={(e) =>
                      handleCheckboxChange("children", "CHILD", index, e)
                    }
                  />
                  <h5>{getChildDisplayName(child, index)}</h5>
                </div>
                {formik.values.children[index].isSelected && (
                  <div className="form">
                    {renderPersonalInfoSection(index)}
                    {renderPassportInfoSection(index)}
                    {(ChildRequireDetails?.Passport_Number ||
                      ChildRequireDetails?.Passport_Expiry ||
                      ChildRequireDetails?.PanCard_No ||
                      ChildRequireDetails?.Nationality) &&
                      renderAdditionalPassportInfoSection(index)}
                    {ChildRequireDetails?.Student_Id &&
                      renderStudentInfoSection(index)}
                  </div>
                )}
              </div>
            ))}
            <a
              className="add-more"
              href="javascript:void(0)"
              onClick={() => {
                if (
                  CHILD > getSelectedItems(formik?.values?.children)?.length
                ) {
                  push({
                    isSelected: true,
                    first_name: "",
                    last_name: "",
                    initial: "",
                    date_of_birth: "",
                    student_id: "",
                    nationality: "",
                    passport_no: "",
                    issue_country_code: "",
                    expire_date: "",
                  });
                } else {
                  formik.setFieldError(
                    "childrenLimit",
                    `You have already selected ${CHILD} CHILD. Remove before adding a new one.`
                  );
                }
              }}
            >
              + Add New Child
            </a>
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default ChildForm;
